/**
 * FetchHttpClient – production-ready Fetch API implementation of IHttpClient.
 *
 * SOLID:
 *  SRP – solely responsible for making HTTP requests via the Fetch API.
 *  OCP – behaviour is extended through interceptor chains, not by subclassing.
 *  LSP – fully substitutable for any IHttpClient consumer.
 *  ISP – implements only the IHttpClient contract.
 *  DIP – depends on the IHttpClient interface and HttpClientConfig; callers
 *        inject configuration rather than hardcoded values.
 *
 * DRY:
 *  All request execution flows through the single private `execute` method.
 *  Retry, timeout, interceptors, and error mapping are applied in one place.
 */

import type {
    HttpClientConfig,
    HttpMethod,
    HttpRequest,
    HttpResponse,
    QueryParams,
    RequestInterceptor,
    ResponseInterceptor,
    ErrorInterceptor,
} from './types';
import type { IHttpClient } from './http-client.interface';
import { buildQueryString } from './query-builder';
import {
    HttpMaxRetriesError,
    HttpNetworkError,
    HttpResponseError,
    HttpTimeoutError,
} from './http-error';

/** Delay helper used by the retry back-off logic. */
const delay = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Compute the next back-off delay with full jitter.
 *
 * Formula: random value in [0, min(cap, base * 2^attempt)]
 * This follows the AWS "Full Jitter" strategy that avoids thundering-herd
 * problems while still converging quickly for low-contention scenarios.
 */
function computeBackoffDelay(attempt: number, baseDelayMs: number): number {
    const cap = 30_000; // max 30 s
    const exponential = baseDelayMs * Math.pow(2, attempt);
    const capped = Math.min(cap, exponential);
    return Math.random() * capped; // full jitter
}

/** Extract response headers into a plain string–string map. */
function extractHeaders(headers: Headers): Record<string, string> {
    const result: Record<string, string> = {};
    headers.forEach((value, key) => {
        result[key] = value;
    });
    return result;
}

/** Returns true when the HTTP status code indicates the request should not be retried. */
function isNonRetryableStatus(status: number): boolean {
    // 4xx errors (except 429 Too Many Requests) are not worth retrying
    return status >= 400 && status < 500 && status !== 429;
}

export class FetchHttpClient implements IHttpClient {
    private readonly baseUrl: string;
    private readonly defaultTimeoutMs: number;
    private readonly maxRetries: number;
    private readonly retryBaseDelayMs: number;
    private readonly defaultHeaders: Record<string, string>;
    private readonly requestInterceptors: RequestInterceptor[];
    private readonly responseInterceptors: ResponseInterceptor[];
    private readonly errorInterceptors: ErrorInterceptor[];

    constructor(config: HttpClientConfig) {
        this.baseUrl = config.baseUrl.replace(/\/+$/, ''); // strip trailing slash
        this.defaultTimeoutMs = config.defaultTimeoutMs ?? 30_000;
        this.maxRetries = config.maxRetries ?? 3;
        this.retryBaseDelayMs = config.retryBaseDelayMs ?? 300;
        this.defaultHeaders = config.defaultHeaders ?? {};
        this.requestInterceptors = config.requestInterceptors ?? [];
        this.responseInterceptors = config.responseInterceptors ?? [];
        this.errorInterceptors = config.errorInterceptors ?? [];
    }

    // ─── Public IHttpClient methods ───────────────────────────────────────────

    get<TData = unknown>(
        url: string,
        request: Omit<HttpRequest, 'url' | 'method'> = {}
    ): Promise<HttpResponse<TData>> {
        return this.execute<TData>({ ...request, url, method: 'GET' });
    }

    post<TData = unknown, TBody = unknown>(
        url: string,
        body?: TBody,
        request: Omit<HttpRequest<TBody>, 'url' | 'method' | 'body'> = {}
    ): Promise<HttpResponse<TData>> {
        return this.execute<TData>({ ...request, url, method: 'POST', body });
    }

    put<TData = unknown, TBody = unknown>(
        url: string,
        body?: TBody,
        request: Omit<HttpRequest<TBody>, 'url' | 'method' | 'body'> = {}
    ): Promise<HttpResponse<TData>> {
        return this.execute<TData>({ ...request, url, method: 'PUT', body });
    }

    patch<TData = unknown, TBody = unknown>(
        url: string,
        body?: TBody,
        request: Omit<HttpRequest<TBody>, 'url' | 'method' | 'body'> = {}
    ): Promise<HttpResponse<TData>> {
        return this.execute<TData>({ ...request, url, method: 'PATCH', body });
    }

    delete<TData = unknown>(
        url: string,
        request: Omit<HttpRequest, 'url' | 'method'> = {}
    ): Promise<HttpResponse<TData>> {
        return this.execute<TData>({ ...request, url, method: 'DELETE' });
    }

    // ─── Core execution pipeline ──────────────────────────────────────────────

    /**
     * Central execution method – all public helpers delegate here.
     *
     * Pipeline:
     *  1. Run request interceptors
     *  2. Attempt request (with timeout)
     *  3. On transient failure: wait (back-off) then retry
     *  4. Run response / error interceptors
     */
    private async execute<TData>(rawRequest: HttpRequest): Promise<HttpResponse<TData>> {
        const timeoutMs = rawRequest.timeoutMs ?? this.defaultTimeoutMs;
        const maxAttempts = (rawRequest.retries ?? this.maxRetries) + 1;

        let lastError: Error = new Error('Unknown error');

        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            if (attempt > 0) {
                const backoff = computeBackoffDelay(attempt - 1, this.retryBaseDelayMs);
                await delay(backoff);
            }

            // Re-apply request interceptors on every attempt so that dynamic
            // values (e.g. a freshly-refreshed auth token) are picked up on retry.
            const request = await this.applyRequestInterceptors(rawRequest);

            try {
                const response = await this.fetchWithTimeout<TData>(request, timeoutMs);
                return await this.applyResponseInterceptors(response);
            } catch (err) {
                lastError = err instanceof Error ? err : new Error(String(err));

                // Apply error interceptors on each attempt
                lastError = await this.applyErrorInterceptors(lastError);

                // Do not retry non-retryable HTTP errors
                if (
                    lastError instanceof HttpResponseError &&
                    isNonRetryableStatus(lastError.status)
                ) {
                    throw lastError;
                }

                // Do not retry on timeout if we have used all attempts
                if (attempt === maxAttempts - 1) {
                    break;
                }
            }
        }

        if (maxAttempts > 1) {
            throw new HttpMaxRetriesError(rawRequest.url, maxAttempts);
        }

        throw lastError;
    }

    // ─── Fetch + timeout ──────────────────────────────────────────────────────

    private async fetchWithTimeout<TData>(
        request: HttpRequest,
        timeoutMs: number
    ): Promise<HttpResponse<TData>> {
        const controller = new AbortController();
        const timerId = setTimeout(() => controller.abort(), timeoutMs);

        try {
            const fullUrl = this.buildUrl(request.url, request.params);
            const headers = this.mergeHeaders(request.headers);
            const fetchInit = this.buildFetchInit(request.method, request.body, headers, controller.signal);

            const raw = await fetch(fullUrl, fetchInit);

            if (!raw.ok) {
                const body = await this.safeParseBody(raw);
                const message = this.extractErrorMessage(body) ?? `HTTP ${raw.status} ${raw.statusText}`;
                throw new HttpResponseError(raw.status, message, body);
            }

            const data = (await this.safeParseBody(raw)) as TData;

            return {
                data,
                status: raw.status,
                headers: extractHeaders(raw.headers),
            };
        } catch (err) {
            if ((err as Error).name === 'AbortError') {
                throw new HttpTimeoutError(request.url, timeoutMs);
            }

            if (err instanceof HttpResponseError || err instanceof HttpTimeoutError) {
                throw err;
            }

            throw new HttpNetworkError((err as Error).message, err);
        } finally {
            clearTimeout(timerId);
        }
    }

    // ─── Helpers ──────────────────────────────────────────────────────────────

    private buildUrl(path: string, params?: QueryParams): string {
        const normalizedPath = path.startsWith('/') ? path : `/${path}`;
        const queryString = params ? buildQueryString(params) : '';
        return `${this.baseUrl}${normalizedPath}${queryString}`;
    }

    private mergeHeaders(requestHeaders?: Record<string, string>): Record<string, string> {
        return {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            ...this.defaultHeaders,
            ...requestHeaders,
        };
    }

    private buildFetchInit(
        method: HttpMethod,
        body: unknown,
        headers: Record<string, string>,
        signal: AbortSignal
    ): RequestInit {
        const init: RequestInit = { method, headers, signal };

        if (body !== undefined && body !== null) {
            init.body = JSON.stringify(body);
        }

        return init;
    }

    private async safeParseBody(response: Response): Promise<unknown> {
        const text = await response.text();
        if (!text) return null;

        try {
            return JSON.parse(text);
        } catch {
            return text;
        }
    }

    private extractErrorMessage(body: unknown): string | undefined {
        if (typeof body === 'string') return body;
        if (typeof body === 'object' && body !== null) {
            const b = body as Record<string, unknown>;
            const msg = b['message'] ?? b['Message'] ?? b['error'] ?? b['Error'];
            if (typeof msg === 'string') return msg;
        }
        return undefined;
    }

    // ─── Interceptor runners ──────────────────────────────────────────────────

    private async applyRequestInterceptors(request: HttpRequest): Promise<HttpRequest> {
        let current = request;
        for (const interceptor of this.requestInterceptors) {
            current = await interceptor(current);
        }
        return current;
    }

    private async applyResponseInterceptors<TData>(
        response: HttpResponse<TData>
    ): Promise<HttpResponse<TData>> {
        let current = response as HttpResponse;
        for (const interceptor of this.responseInterceptors) {
            current = await interceptor(current);
        }
        return current as HttpResponse<TData>;
    }

    private async applyErrorInterceptors(error: Error): Promise<Error> {
        let current = error;
        for (const interceptor of this.errorInterceptors) {
            current = await interceptor(current);
        }
        return current;
    }
}
